/**
 * Where a notice's attachments sit inside its text.
 *
 * The mobile composer gives each anexo a visible marker in the message — `[anexo 1]` — so the
 * síndico can say WHERE a photo or a document appears in the aviso. The markers live in the body
 * text itself, so there is no schema change and nothing to migrate; a notice with no markers (one
 * written before this, or posted from this screen) renders exactly as it always did, with its
 * attachments after the text.
 *
 * This is the web's port of the apps' `MuralAttachments.swift` / `MuralAttachments.kt`. The three
 * must agree — the same notice is read on all of them — so the rules here are deliberately dull:
 * see muralBody.test.ts.
 */

export type MuralBodyBlock =
  | { kind: 'text'; text: string }
  /** 1-based, matching what the marker says. */
  | { kind: 'attachment'; number: number };

/** Any case, any inner spacing — the síndico edits this text by hand. */
const MARKER = /\[\s*anexo\s+(\d+)\s*\]/gi;

// A plain array rather than a generator: this project's tsconfig targets ES5, where iterating a
// generator needs --downlevelIteration. A notice has at most six attachments, so there is nothing
// to stream.
function markers(body: string): Array<{ index: number; length: number; number: number }> {
  const found: Array<{ index: number; length: number; number: number }> = [];
  MARKER.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKER.exec(body)) !== null) {
    const number = Number(match[1]);
    if (Number.isFinite(number)) {
      found.push({ index: match.index, length: match[0].length, number });
    }
  }
  return found;
}

/**
 * Splits a body into text and attachment placements.
 *
 * A marker naming an attachment that is not there is left as LITERAL TEXT rather than dropped:
 * showing `[anexo 3]` is how the síndico finds out it is stale, where swallowing it would lose a
 * line of their notice.
 */
export function muralBodyBlocks(body: string, attachmentCount: number): MuralBodyBlock[] {
  const blocks: MuralBodyBlock[] = [];
  let cursor = 0;

  const pushText = (raw: string) => {
    const text = raw.trim();
    // Empty runs between two markers would otherwise render as a blank paragraph.
    if (text) blocks.push({ kind: 'text', text });
  };

  for (const marker of markers(body)) {
    if (marker.number < 1 || marker.number > attachmentCount) continue;
    pushText(body.slice(cursor, marker.index));
    blocks.push({ kind: 'attachment', number: marker.number });
    cursor = marker.index + marker.length;
  }
  pushText(body.slice(cursor));

  return blocks;
}

/** The attachments no marker placed — rendered after the text, in order. */
export function muralUnreferencedAttachments(body: string, attachmentCount: number): number[] {
  const placed = new Set<number>();
  for (const marker of markers(body)) placed.add(marker.number);
  return Array.from({ length: Math.max(attachmentCount, 0) }, (_, i) => i + 1)
    .filter(number => !placed.has(number));
}

/**
 * The body with its markers taken out — what counts as TEXT.
 *
 * `[anexo 1]` is a position, not prose: a photo with no caption has no text, however many markers
 * hold its position.
 */
export function muralBodyWithoutMarkers(body: string): string {
  return body.replace(MARKER, '').trim();
}

/**
 * The one-line excerpt a notice shows in a list.
 *
 * Strips the `[anexo N]` markers: they are positions, not prose, and a list row that reads
 * "[anexo 1] [anexo 2]" tells the operator nothing about the aviso. When the markers were ALL the
 * body had, the attachment itself is the summary — the document's name, or how many photos.
 */
export function muralListExcerpt(
  anuncio: { body?: string | null; pdf_url?: string | null; pdf_name?: string | null },
  photoCount: number,
): string {
  const text = muralBodyWithoutMarkers(anuncio.body ?? '');
  if (text) return text;
  if (anuncio.pdf_url) return anuncio.pdf_name || 'Documento PDF';
  if (photoCount === 1) return '1 foto';
  if (photoCount > 1) return `${photoCount} fotos`;
  return '';
}
